import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Drawer, Empty, Segmented, Skeleton, Table, Tag, Tooltip, Typography } from 'antd';
import { RobotOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { StackedColumns } from '@/features/insights/InsightsViz';
import { CallCostStates, type AiSpendCallRow, type AiSpendRunRow } from '@/api/aiSpend';
import type { CostsGrain } from '@/stores/CostsStore';
import { count, dayLabel, instantUtc, modelColor, monthLabel, usd } from './costsFormat';

const { Text } = Typography;

// Drawers over modals (house rule): an agent's spend, and the calls of one run.

export const AgentSpendDrawer = observer(function AgentSpendDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { costsStore: s, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const d = s.agentDetail;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={620}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <RobotOutlined style={{ color: colors.ink[600] }} />
          <span>{d?.agent.name ?? 'Agent'}</span>
          {d?.agent.canonicalName && <Text type="secondary" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{d.agent.canonicalName}</Text>}
        </span>
      }
    >
      {s.agentError && <Alert type="error" showIcon message="This agent could not be loaded" description={s.agentError} />}
      {s.agentLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            <Mini label={`spend · ${d.agent.sharePct}% of all`} value={usd(d.agent.spendUsd)} isDark={isDark} />
            <Mini label="runs" value={count(d.agent.runs)} isDark={isDark} />
            <Mini label="median per run" value={d.agent.medianRunUsd == null ? '—' : usd(d.agent.medianRunUsd)} isDark={isDark}
              hint={d.agent.medianRunUsd == null ? 'Fewer than 10 fully priced runs in this range' : undefined} />
            <Mini label="unusual runs" value={count(d.agent.unusualRuns)} isDark={isDark} />
          </div>

          {d.agent.callsMissingCost > 0 && (
            <Alert type="warning" showIcon message={`${count(d.agent.callsMissingCost)} of this agent's ${count(d.agent.calls)} calls carry no cost, so its spend is a floor.`} />
          )}

          <div>
            <SubTitle>Spend per {s.grain}</SubTitle>
            <GrainSwitch />
            <StackedColumns
              height={170}
              series={d.trend.models}
              colorOf={(m) => modelColor(d.trend.models, m)}
              format={usd}
              data={d.trend[s.grain].map((p) => ({
                key: p.start,
                label: s.grain === 'month' ? monthLabel(p.start) : dayLabel(p.start),
                title: s.grain === 'month' ? monthLabel(p.start) : s.grain === 'week' ? `Week of ${dayLabel(p.start, true)}` : dayLabel(p.start, true),
                values: p.byModel.map((m) => ({ series: m.model, value: m.usd })),
                footnote: `${count(p.calls)} calls${p.callsMissingCost ? ` · ${count(p.callsMissingCost)} without a cost` : ''}`,
              }))}
            />
          </div>

          <div>
            <SubTitle>Models</SubTitle>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {d.models.map((m) => (
                <Tag key={m.model} color={undefined} style={{ borderColor: modelColor(d.trend.models, m.model), padding: '2px 8px' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: modelColor(d.trend.models, m.model), marginRight: 6 }} />
                  {m.model} · {usd(m.usd)} · {count(m.calls)} calls
                </Tag>
              ))}
            </div>
          </div>

          <div>
            <SubTitle>Costliest runs</SubTitle>
            <RunTable rows={d.costliestRuns} showAgent={false} />
          </div>
        </div>
      )}
    </Drawer>
  );
});

export const RunCallsDrawer = observer(function RunCallsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { costsStore: s } = useStore();
  const r = s.runDetail;
  const [whole, setWhole] = useState(false);
  // A newly opened run starts on the share that was clicked.
  useEffect(() => setWhole(false), [r?.runKey, r?.focus?.agentId]);
  const showingShare = !!r?.focus && !whole;
  const rows = r ? (showingShare ? r.calls.filter((c) => c.agentId === r.focus!.agentId) : r.calls) : [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={780}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <PlayCircleOutlined style={{ color: colors.ink[600] }} />
          <span>
            {r?.focus ? <>{r.focus.agentName} · {usd(r.focus.usd)} <Text type="secondary" style={{ fontWeight: 400 }}>of a {usd(r.usd)} request</Text></> : <>Run · {r ? usd(r.usd) : ''}</>}
          </span>
        </span>
      }
    >
      {s.runError && <Alert type="error" showIcon message="This run could not be loaded" description={s.runError} />}
      {s.runLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {r && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            {r.focus
              ? <>One request made {count(r.calls.length)} calls across {count(r.agents)} {r.agents === 1 ? 'agent' : 'agents'}; {count(r.focus.calls)} of them were this agent's. </>
              : <>Every call made for one request, in order. </>}
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.runKey}</span>
          </Text>
          {r.focus && (
            <Segmented<'share' | 'whole'>
              value={whole ? 'whole' : 'share'}
              onChange={(v) => setWhole(v === 'whole')}
              options={[
                { label: `This agent · ${count(r.focus.calls)} calls · ${usd(r.focus.usd)}`, value: 'share' },
                { label: `Whole request · ${count(r.calls.length)} calls · ${usd(r.usd)}`, value: 'whole' },
              ]}
              style={{ alignSelf: 'flex-start' }}
            />
          )}
          {!r.complete && <Alert type="warning" showIcon message="Some calls in this request carry no cost, so its total is a floor." />}
          <Table<AiSpendCallRow>
            size="small"
            rowKey="id"
            pagination={rows.length > 50 ? { pageSize: 50, size: 'small', showSizeChanger: false } : false}
            dataSource={rows}
            columns={[
              { title: 'When', dataIndex: 'createdUtc', render: (v: string) => instantUtc(v), width: 130 },
              ...(showingShare ? [] : [{ title: 'Agent', dataIndex: 'agentName', ellipsis: true }]),
              { title: 'Model', dataIndex: 'model', width: 120 },
              { title: 'Tokens in / out', align: 'right' as const, render: (_: unknown, c: AiSpendCallRow) => `${count(c.inputTokens)} / ${count(c.outputTokens)}`, width: 130 },
              { title: 'Tools', dataIndex: 'toolCalls', align: 'right' as const, width: 60 },
              { title: 'Time', dataIndex: 'durationMs', align: 'right' as const, render: (v: number) => `${(v / 1000).toFixed(1)} s`, width: 70 },
              { title: 'Cost', align: 'right' as const, width: 110, render: (_: unknown, c: AiSpendCallRow) => <CallCost c={c} /> },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
});

function CallCost({ c }: { c: AiSpendCallRow }) {
  if (c.costState === CallCostStates.NoRate) return <Tooltip title="No rate on file for this model when the call was written"><Tag color="gold">no rate</Tag></Tooltip>;
  if (c.costState === CallCostStates.StoredAsZero) return <Tooltip title="Tokens were used but $0.00 was stored (older rows were rounded to whole cents)"><Tag color="gold">stored $0.00</Tag></Tooltip>;
  return (
    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {usd(c.usd)}
      {c.hasError && <Tag color="red" style={{ marginLeft: 6 }}>{c.statusCode}</Tag>}
    </span>
  );
}

/** Runs table shared by the page (unusual runs) and the agent drawer (costliest runs). */
export const RunTable = observer(function RunTable({ rows, showAgent }: { rows: AiSpendRunRow[]; showAgent: boolean }) {
  const { costsStore: s } = useStore();
  const limit = s.report?.unusualMultiple;
  if (rows.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No runs to show" />;
  return (
    <Table<AiSpendRunRow>
      size="small"
      rowKey={(r) => `${r.agentId ?? 'none'}-${r.runKey}`}
      pagination={rows.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: false } : false}
      dataSource={rows}
      onRow={(r) => ({ onClick: () => void s.openRun(r.runKey, r.agentId), style: { cursor: 'pointer' } })}
      columns={[
        ...(showAgent ? [{ title: 'Agent', dataIndex: 'agentName', ellipsis: true, render: (v: string) => <b>{v}</b> }] : []),
        { title: 'When', dataIndex: 'startedUtc', render: (v: string) => instantUtc(v), width: 140 },
        { title: 'Calls', dataIndex: 'calls', align: 'right' as const, width: 70 },
        { title: 'Tool calls', dataIndex: 'toolCalls', align: 'right' as const, width: 90 },
        { title: 'Cost', dataIndex: 'usd', align: 'right' as const, width: 90, render: (v: number) => <b style={{ fontVariantNumeric: 'tabular-nums' }}>{usd(v)}</b> },
        {
          title: 'vs usual',
          dataIndex: 'multiple',
          align: 'right' as const,
          width: 110,
          render: (m: number | null, r: AiSpendRunRow) =>
            m == null ? <Text type="secondary">—</Text> : (
              <Tooltip title={`This agent's usual run costs ${usd(r.usualUsd)}`}>
                <Tag color={limit != null && m > limit ? 'red' : 'default'} style={{ marginInlineEnd: 0 }}>{m}× usual</Tag>
              </Tooltip>
            ),
        },
      ]}
    />
  );
});

export const GrainSwitch = observer(function GrainSwitch() {
  const { costsStore: s } = useStore();
  return (
    <Segmented<CostsGrain>
      size="small"
      value={s.grain}
      onChange={(g) => s.setGrain(g)}
      options={[{ label: 'Day', value: 'day' }, { label: 'Week', value: 'week' }, { label: 'Month', value: 'month' }]}
      style={{ marginBottom: 8 }}
    />
  );
});

function Mini({ label, value, hint, isDark }: { label: string; value: string; hint?: string; isDark: boolean }) {
  const box = (
    <div style={{ background: isDark ? colors.slate[800] : colors.slate[50], border: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`, borderRadius: 10, padding: '8px 10px' }}>
      <div style={{ fontSize: 17, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11, color: colors.slate[500] }}>{label}</div>
    </div>
  );
  return hint ? <Tooltip title={hint}>{box}</Tooltip> : box;
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.slate[500], marginBottom: 6 }}>{children}</div>;
}
